/**
 * 공고 표기 헬퍼 — 금액과 마감일을 사람이 읽는 형태로.
 *
 * `packages/shared` 에 두는 이유: 목록·상세·주간 기사가 **같은 숫자를 같은
 * 모양으로** 보여야 하고, 한 곳에서만 반올림 규칙을 정해야 하기 때문이다.
 */
/**
 * 원 단위 정수 → 사람이 읽는 금액.
 *
 * **버림으로 맞춘다.** 2,297,016원을 `230만원` 으로 올리면 실제보다 많이
 * 준다고 말하는 셈이다. 지원금은 절대 부풀리지 않는다 — 229만원이다.
 *
 * 정부 공고문은 `66,000천원` 같은 단위를 쓰는데 사람은 그렇게 생각하지
 * 않는다. 그래서 원문 표현은 근거로 따로 보여주고 여기서는 억·만 단위로
 * 옮긴다.
 */
export function formatAmountKrw(won) {
    if (won === null || !Number.isFinite(won) || won <= 0) {
        return null;
    }
    const eok = Math.floor(won / 100_000_000);
    const man = Math.floor((won % 100_000_000) / 10_000);
    if (eok > 0) {
        // 천만 단위로 딱 떨어지면 「2억 2,000만원」보다 「2.2억원」이 한국어로 자연스럽다.
        if (man > 0 && man % 1_000 === 0)
            return `${eok}.${man / 1_000}억원`;
        return man > 0 ? `${eok}억 ${man.toLocaleString('ko-KR')}만원` : `${eok}억원`;
    }
    if (man > 0) {
        return `${man.toLocaleString('ko-KR')}만원`;
    }
    // 1만원 미만은 버림이 곧 0 이 되므로 원 단위 그대로 보여준다.
    return `${won.toLocaleString('ko-KR')}원`;
}
/**
 * 마감까지 남은 일수. 마감일이 없으면 null.
 *
 * **한국 날짜로 센다.** UTC 로 세면 한국 00:00–09:00 구간에 하루가 어긋나
 * "오늘 마감"인 공고가 "내일"로 보인다.
 */
export function daysUntilDeadline(applyEnd, now) {
    if (!applyEnd) {
        return null;
    }
    const kstToday = new Date(now.getTime() + 9 * 3_600_000).toISOString().slice(0, 10);
    const a = Date.parse(`${applyEnd}T00:00:00Z`);
    const b = Date.parse(`${kstToday}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b)) {
        return null;
    }
    return Math.round((a - b) / 86_400_000);
}
/**
 * 마감 라벨.
 *
 * 마감일이 없는 공고가 전체 26%(기업마당만 보면 58%)다. 그것을 날짜와 **같은
 * 모양으로** 그리면 독자가 둘을 구별하지 못한다 — 참조로 삼은 기존 UI 가 정확히
 * 그렇게 하고 있었다.
 */
export function deadlineLabel(applyEnd, now) {
    const days = daysUntilDeadline(applyEnd, now);
    if (days === null) {
        return { text: '상시', tone: 'none', days: null };
    }
    if (days < 0) {
        return { text: '마감', tone: 'expired', days };
    }
    if (days === 0) {
        return { text: '오늘 마감', tone: 'urgent', days };
    }
    // 먼 마감도 D-n 이다. 예전엔 90일을 넘으면 날짜(26.12.31)로 바꿨는데, 한 목록에
    // D-16 과 26.12.31 이 섞이면 두 표기를 머릿속에서 환산해야 한다 (2026-09-12 결정).
    return { text: `D-${days}`, tone: days <= 7 ? 'urgent' : days <= 30 ? 'soon' : 'later', days };
}
/**
 * K-Startup 의 창업업력 필드(`biz_enyy`)를 사람이 읽는 말로.
 *
 * 출처는 허용 구간을 **쉼표 목록**으로 준다 — 「예비창업자,1년미만,2년미만,3년미만,5년미만,7년미만,10년미만」.
 * 원문 사이트는 일곱 구간이 다 있으면 「전체」 라고 보여주는데 우리는 목록을 그대로 찍었다(실측
 * 2,269건). 이어진 구간은 「예비창업자 ~ 7년 미만」 처럼 범위로, 띄엄띄엄이면 「·」 로 잇는다.
 * 목록이 아닌 값(기업마당의 산문)은 그대로 돌려준다.
 */
const AGE_BANDS = ['예비창업자', '1년미만', '2년미만', '3년미만', '5년미만', '7년미만', '10년미만'];
const bandLabel = (b) => (b === '예비창업자' ? b : b.replace('년미만', '년 미만'));
export function businessAgeLabel(raw) {
    if (raw === null)
        return null;
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    const idx = parts.map((p) => AGE_BANDS.indexOf(p));
    if (parts.length === 0 || idx.some((i) => i < 0))
        return raw;
    const sorted = [...new Set(idx)].sort((a, b) => a - b);
    if (sorted.length === AGE_BANDS.length)
        return '전체';
    const contiguous = sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
    const first = AGE_BANDS[sorted[0]];
    const last = AGE_BANDS[sorted[sorted.length - 1]];
    if (sorted.length === 1)
        return bandLabel(first);
    // 예비창업자부터 이어지면 「예비창업자 ~ N년 미만」, 1년미만부터 이어지면 그냥 「N년 미만」(업력 있는 기업 전부).
    if (contiguous && first === '예비창업자')
        return `예비창업자 ~ ${bandLabel(last)}`;
    if (contiguous && first === '1년미만')
        return bandLabel(last);
    return sorted.map((i) => bandLabel(AGE_BANDS[i])).join(' · ');
}
//# sourceMappingURL=grant-format.js.map